import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { PRODUCTS, SHOP_CATEGORIES } from '../mock/mock';
import { useCart } from '../context/CartContext';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function ShopPage() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const initialCat = params.get('cat') || 'all';
  const [cat, setCat] = useState(initialCat);
  const [sort, setSort] = useState('featured');
  const { addItem } = useCart();

  useEffect(() => { setCat(initialCat); }, [initialCat]);

  const products = useMemo(() => {
    let list = cat === 'all' ? [...PRODUCTS] : PRODUCTS.filter((p) => p.category === cat);
    if (sort === 'low') list.sort((a, b) => a.price - b.price);
    if (sort === 'high') list.sort((a, b) => b.price - a.price);
    if (sort === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [cat, sort]);

  const handleAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(p, 1, 'M');
    toast(`${p.name} added to bag`);
  };

  return (
    <main className="bg-cream">
      {/* Header */}
      <header className="px-5 md:px-10 pt-10 md:pt-16 pb-8 border-b border-brown/15 max-w-[1600px] mx-auto">
        <p className="font-anton uppercase tracking-[0.3em] text-pink-hot text-xs mb-4">— Official Shop</p>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="mega text-brown text-6xl md:text-[9rem] leading-[0.85]">
            {cat === 'all' ? 'All Products' : SHOP_CATEGORIES.find((c) => c.slug === cat)?.label}
          </h1>
          <p className="font-body text-brown/70 text-sm">{products.length} items</p>
        </div>
      </header>

      {/* Filters bar */}
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-brown/15 sticky top-16 md:top-20 bg-cream/95 backdrop-blur z-20">
        <div className="flex flex-wrap gap-2">
          {SHOP_CATEGORIES.map((c) => (
            <button key={c.slug} onClick={() => setCat(c.slug)}
              className={`font-anton uppercase tracking-widest text-xs px-4 py-2 border transition-colors rounded-full ${
                cat === c.slug ? 'bg-brown text-cream border-brown' : 'bg-transparent text-brown border-brown/30 hover:border-brown'
              }`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <label className="font-anton uppercase tracking-widest text-xs text-brown/70">Sort</label>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            className="font-body text-sm border border-brown/30 bg-cream text-brown px-3 py-2 focus:outline-none focus:border-brown rounded-full">
            <option value="featured">Featured</option>
            <option value="low">Price: Low to High</option>
            <option value="high">Price: High to Low</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>
      </div>

      {/* Product grid */}
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {products.map((p) => (
            <Link to={`/shop/${p.slug}`} key={p.id} className="product-card group block">
              <div className={`relative aspect-[4/5] overflow-hidden ${
                p.color === 'pink' ? 'bg-pink' : p.color === 'green' ? 'bg-green' : p.color === 'brown' ? 'bg-brown' : 'bg-cream-2'
              }`}>
                <img src={p.images[0]} alt={p.name} className="product-img w-full h-full object-cover" />
                {p.badge && (
                  <span className="absolute top-3 left-3 font-anton uppercase tracking-widest text-[10px] bg-cream text-brown px-2 py-1 rounded-full">{p.badge}</span>
                )}
                <button onClick={(e) => handleAdd(e, p)}
                  className="absolute bottom-3 right-3 w-10 h-10 bg-cream text-brown flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-pink-hot hover:text-cream rounded-full"
                  aria-label="add to cart">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="mt-3 flex items-start justify-between gap-2">
                <h3 className="font-body text-brown text-sm md:text-[15px] leading-snug">{p.name}</h3>
                <p className="font-display text-brown text-sm md:text-base whitespace-nowrap">${p.price.toFixed(2)}</p>
              </div>
              <p className="font-body text-[11px] uppercase tracking-widest text-brown/60 mt-1">{p.category}</p>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
